import { createDealerAxios } from '../factories/axios-factory';
import { useAuthStore } from '../stores/auth-store';

import type { VehicleSearchResponse} from 'src/types/vehicle-search';
import type { CapValuationResponse } from 'src/types/valuations';
import type { Vehicle, CapVehicle, SavedVehicle } from 'src/types/loan-application';

import { Customer, DealerType, DrivingLicence, EmploymentStatus, isFinanceEligible, Vehicle as PricingVehicle, ResidentialStatus } from "@cf247/pricing"
import { mockAlpheraProposal, mockCaProposal, mockCloseProposal, mockMotoNovoProposal, mockNorthridgeProposal, mockZopaProposal } from '@cf247/pricing/dist/mocks';


const dealerBackendDealer = createDealerAxios(process.env.DealersApiUrl!);

const vehicleService = {
  vehicleLookup(reg: string, mileage?: number, vehicleIdNeeded = false, getCapId = false) {
    return new Promise<Vehicle>(async (resolve, reject) => {
      let vehicle = null as Vehicle | null;

      // First try vehicle search
      const searchTask = dealerBackendDealer.get(
        `/vehicle/reg/internal?reg=${reg.toUpperCase().replace(" ", "")}`
      ).then(res => {
          if (res.data.vehicleId) {
            vehicle = {
              vehicleId: res.data.vehicleId,
              make: res.data.make,
              model: res.data.model,
              trim: res.data.trim,
              vehicleDateOfFirstReg: res.data.registrationDate ? new Date(res.data.registrationDate) : null,
              regYear: res.data.regYear,
              vrm: res.data.regNumber,
              fuel: res.data.fuel,
              colour: res.data.colour,
              mileage: res.data.mileage,
              price: res.data.price,
              transmission: res.data.trans,
              isIntranetLoanVehicle: false,
              year: res.data.registrationDate ? new Date(res.data.registrationDate).getFullYear() : null,
              futures: res.data.vehicleResiduals ? JSON.parse(res.data.vehicleResiduals) : null,
              vehicleResiduals: res.data.vehicleResiduals,
              capCode: "",
              capRetail: res.data.capRetail,
              capAverage: res.data.capAverage,
              capClean: res.data.capClean,
              capCreatedOn: res.data.capCreatedOn,
              capId: res.data.capId,
              image: res.data.image,
              images: res.data.images,
              vin: res.data.vin,
            } as Vehicle;
          }
        }).catch(error => {
          console.log("no vehicle internal")
          vehicle = null;
        })
      await searchTask;

      let pcp = false;

      if (vehicle) {
        // check if pcp eligible
        const eligible = await isFinanceEligible(
          [{
            make: vehicle.make,
            model: vehicle.model,
            vrm: vehicle.vrm,
            mileage: vehicle.mileage,
            registrationDate: vehicle.vehicleDateOfFirstReg ? new Date(vehicle.vehicleDateOfFirstReg) : vehicle.vehicleDateOfFirstReg ? new Date(vehicle.registrationDate) : null,
            price: vehicle.price
          } as PricingVehicle],
          [
            mockAlpheraProposal(9.9),
            mockCaProposal(9.9),
            mockCloseProposal(9.9),
            mockMotoNovoProposal(9.9),
            mockNorthridgeProposal(9.9),
            mockZopaProposal(9.9)
          ],
          {
            drivingLicence: DrivingLicence.FullUK,
            dateOfBirth: new Date(1990, 1, 1),
            firstName: "John",
            surname: "Smith",
            netMonthlyIncome: 3000,
            employmentHistory: [{
              index: 0,
              employer: "Company A",
              occupation: "Engineer",
              yearsAt: 5,
              monthsAt: 0,
              status: EmploymentStatus.EmployedFullTime
            }],
            addressHistory: [{
              index: 0,
              street: "123 Main St",
              town: "Anytown",
              postcode: "AB12 3CD",
              yearsAt: 5,
              monthsAt: 0,
              status: ResidentialStatus.HomeOwner
            }]
          } as Customer,
          {
            type: DealerType.NonFranchisedDealer
          },
          60,
          0,
          0,
          0,
          8000
        );

        if(eligible && eligible.length > 0 && eligible[0].pcp && eligible[0].pcp.length > 0){
          pcp = true;
        }
        
      }
      else if (mileage && mileage < 80000) {
        pcp = true;
      }
      else {
        pcp = false;
      }

      if(getCapId){
        pcp = true;
      }

      // Do a cap lookup
      const capTask = dealerBackendDealer.get(
        !vehicleIdNeeded ?
        `/vehicle-cap/${reg}/${pcp}/${useAuthStore().isIntranetUser ? 'CF247-User-Dealer-Portal' : (useAuthStore().affiliateUserId ?? useAuthStore().affiliateUserIds[0]) }` : 
        `/vehicle/reg/experian?reg=${reg}`
      ).then(async (res) => {
        const capVehicle = {
          vehicleId: res.data.vehicleId,
          vin: !vehicleIdNeeded ? res.data.vin : res.data.vehicleVin,
          vrm: !vehicleIdNeeded ? (res.data.vrm ?? res.data.vehicleRegistration) : res.data.registration,
          capId: res.data.capId,
          capCode: res.data.capCode,
          mileage: res.data.mileage,
          make: res.data.make,
          model: res.data.model,
          colour: !vehicleIdNeeded ? res.data.colour : res.data.vehicleColour,
          vehicleDateOfFirstReg: !vehicleIdNeeded ? (res.data.vehicleDateOfFirstReg ? new Date(res.data.vehicleDateOfFirstReg) : null ) : res.data.dateOfFirstReg ? new Date(res.data.dateOfFirstReg) : null,
          fuelType: res.data.fuelType,
          price: res.data.otrSalePrice,
          transmission: !vehicleIdNeeded ? res.data.transmission : res.data.transmissionType,
        } as CapVehicle;

        if(vehicle && !vehicle.vehicleDateOfFirstReg && capVehicle && capVehicle.vehicleDateOfFirstReg){
          vehicle.vehicleDateOfFirstReg = capVehicle.vehicleDateOfFirstReg;
        }

        // Rare cases of reg switch, cap lookup for a reg will return
        // a different reg with no capcode, researching by that reg
        // returns a cap code
        if (vehicle && capVehicle.capCode === null) {
          const response = await dealerBackendDealer.get(
            !vehicleIdNeeded ?
            `/vehicle-cap/${capVehicle.vrm}/${pcp}/${useAuthStore().isIntranetUser ? 'CF247-User-Dealer-Portal' : (useAuthStore().affiliateUserId ?? useAuthStore().affiliateUserIds[0])}`:
            `/vehicle/reg/experian?reg=${capVehicle.vrm}`
          )

          const regSwitch = response.data as Vehicle;
          if (vehicle && !vehicle.capCode) {
            vehicle.capCode = capVehicle.capCode;//
          }
        }
        // if just missing capcode, use that
        else if (vehicle) {
          vehicle.capCode = capVehicle.capCode;
          vehicle.capId = capVehicle.capId;
        }
        else {
          // Use the cap lookup vehicle
          if (capVehicle && capVehicle.vehicleDateOfFirstReg) {
            capVehicle.vehicleDateOfFirstReg = new Date(capVehicle.vehicleDateOfFirstReg);
          }
          // Some rare cases of cap lookup only returning a date of reg, no make / model
          // Do not allow to quote
          if (capVehicle.make) {
            vehicle = {
              vehicleId: capVehicle.vehicleId,
              make: capVehicle.make,
              model: capVehicle.model,
              mileage: mileage && mileage > 0 ? mileage : null,
              vrm: capVehicle.vrm,
              vehicleDateOfFirstReg: capVehicle.vehicleDateOfFirstReg,
              price: capVehicle.price,
              vehicleCondition: capVehicle.vehicleCondition,
              fuel: capVehicle.fuelType,
              transmission: capVehicle.transmission,
              annualMileage: capVehicle.annualMileage,
              capId: capVehicle.capId,
              capCode: capVehicle.capCode,
              capCreatedOn: capVehicle.capCreatedOn ? new Date(capVehicle.capCreatedOn) : null,
              capAverage: capVehicle.capAverage,
              capClean: capVehicle.capClean,
              capRetail: capVehicle.capRetail,
              colour: capVehicle.colour,
              vin: capVehicle.vin,
              isIntranetLoanVehicle: true,
              year: capVehicle.vehicleDateOfFirstReg ? capVehicle.vehicleDateOfFirstReg.getFullYear() : null,
            } as Vehicle;
          }
        }
      }).catch(error => {
        console.log("no vehicle external");
      })

      await capTask;
 
      if (vehicle) {
        if(!vehicle.isIntranetLoanVehicle){
          vehicle.image = 'https://res.cloudinary.com/carfinance247-co-uk/image/upload/q_10/v1/' + vehicle.vehicleId + '/1.webp'
        }

        resolve(vehicle);
      }

      reject(null);
     
    })
  },
  capValuation(capId: number, mileage: number, reg: string, year: number | undefined) {
    return new Promise<CapValuationResponse>((resolve, reject) => {
      dealerBackendDealer.get(
        `/cap-valuations?capId=${capId}&mileage=${mileage}&vrm=${reg.toUpperCase().replace(" ", "")}&year=${year}`).then(res => {
          if (res.status === 200) {
            resolve(res.data as CapValuationResponse);
          }
        }).catch(error => {
          console.log("no valuations found!");
          reject(null)
        })
    })
  },
  getStockVehicles(dealerId: number) {
    if (dealerId === 46485) {
      dealerId = 38539;
    }
    if (dealerId === 50235) {
      dealerId = 44894;
    }
    if (dealerId === 52336) {
      dealerId = 48237;
    }
    if (dealerId === 52628) {
      dealerId = 44894;
    }

    
    return new Promise<VehicleSearchResponse>((resolve, reject) => {
      dealerBackendDealer.post(
        `/vehicle/stock-prod`,
        {
          postcode: null,
          mileage: null,
          transId: null,
          priceMin: null,
          priceMax: null,
          engineMin: null,
          engineMax: null,
          colourIds: [],
          dealerId,
          pageSize: 999,
          page: 1,
          searchPublisherIds: [1, 5]
        }
      ).then(res => {
        const r = res.data;
        const mapped = [] as Vehicle[];
        r.vehicles.forEach((v: any) => {
          mapped.push({
            vehicleId: v.vehicleId,
            make: v.make,
            model: v.model,
            trim: v.trim,
            vehicleDateOfFirstReg: v.registrationDate !== null ?
              new Date(v.registrationDate) :
              v.year ?
                new Date(v.year, new Date().getMonth(), new Date().getDate()) :
                v.regYear && v.regYear.toString().length === 2 ?
                  regDateFromRegYear(v.regYear) :
                  null,
            regYear: v.regYear,
            vrm: v.regNumber,
            fuel: v.fuel,
            colour: v.colour,
            mileage: v.mileage,
            price: v.price,
            transmission: v.trans,
            isIntranetLoanVehicle: false,
            images: v.images,
            image: v.image,
            dealerId: v.dealerId,
            year: v.registrationDate ?
              new Date(v.registrationDate).getFullYear() :
              v.year ?
                v.year :
                v.regYear ?
                  regDateFromRegYear(v.regYear)!.getFullYear() :
                  null,
            futures: v.vehicleResiduals ? JSON.parse(v.vehicleResiduals) : null,
          } as Vehicle);
        })
        console.log("Stock vehicles:", mapped);
        resolve({
          pageSize: res.data.pageSize,
          page: res.data.page,
          total: res.data.total,
          vehicles: mapped
        })
      }).catch(error => {
        reject(error);
      })
    })
  },
  getSavedVehicles(customerUserId: number) {
    return new Promise<SavedVehicle[]>((resolve, reject) => {
      dealerBackendDealer.get(
        `/vehicle/saved?customerUserId=${customerUserId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + useAuthStore().token.authenticationToken
          }
        }
      ).then(res => {
        const r = res.data as SavedVehicle[];
        resolve(r);
      }).catch(error => {
        console.log("no saved vehicles found!");
        reject(null);
      })
    })
  },
  saveVehicle(customerUserId: number, vehicleId: number) {
    return new Promise<boolean>((resolve, reject) => {
      dealerBackendDealer.post(
        `/vehicle/saved?customerUserId=${customerUserId}&vehicleId=${vehicleId}`,
        null,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + useAuthStore().token.authenticationToken
          }
        }
      ).then(res => {
        if(res.status === 200){
          resolve(true);
          return;
        }
        resolve(false);
      }).catch(error => {
        console.log("error saving vehicle");
        reject(error);
      })
    })
  },
  saveIntranetLoanVehicle(customerUserId: number, vehicleId: number, mileage: number, price: number) {
    return new Promise<boolean>((resolve, reject) => {
      dealerBackendDealer.post(
        `/vehicle/saved-intranet?customerUserId=${customerUserId}&loanVehicleId=${vehicleId}&mileage=${mileage}&price=${price}`,
        null,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + useAuthStore().token.authenticationToken
          }
        }
      ).then(res => {
        if(res.status === 200){
          resolve(true);
          return;
        }
        resolve(false);
      }).catch(error => {
        console.log("no saved vehicles found!");
        reject(error);
      })
    })
  }
};
function regDateFromRegYear(regYear: number) {
  const split = regYear.toString().split("");
  switch (split[0]) {
    case "0": // 2000s mar-aug
      return new Date(Number("200" + split[1]), 3, 1);
    case "5": // 2000s sep+
      return new Date(Number("200" + split[1]), 9, 1);
    case "1": // 2010s mar-aug
      return new Date(Number("201" + split[1]), 3, 1);
    case "6": // 2010s sep+
      return new Date(Number("201" + split[1]), 9, 1);
    case "2": // 2020s mar-aug
      return new Date(Number("202" + split[1]), 3, 1);
    case "7": // 2020s sep+
      return new Date(Number("202" + split[1]), 9, 1);
  }
}

export default vehicleService;
